<template>
    <app-layout title="Reports">
        <page-header>
            <template #title>Reports - Retail</template>

            <template #actions>
                <jet-tertiary-button type="button" class="py-1" @click="handlePrint" :disabled="form.processing || form.isDirty || !Object.keys(report_data).length" >
                    <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                </jet-tertiary-button>

                <jet-button type="button" @click="handleSubmit" :disabled="form.processing || !canRunReport" >
                    Run Report
                </jet-button>
            </template>
        </page-header>

        <reports-navigation />

        <div class="px-8">
            <div class="flex">
                <form class="flex flex-1 gap-x-2 gap-y-2 flex-wrap" @submit.prevent="handleSubmit">
                    <div class="flex items-end w-full gap-x-2">
                        <div class="flex flex-col w-48">
                            <jet-label for="report" value="Report" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm whitespace-nowrap h-7 text-base"
                                v-model="form.report"
                                :options="reportOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canClear="false"
                                :canDeselect="false"
                                placeholder="Select a Report"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                            />
                        </div>

                        <div class="flex flex-col w-68">
                            <jet-label for="date_range" value="Date Range" />
                            <datepicker inputClassName="dp-custom-input"
                                @update:modelValue="handleUpdateDateRange"
                                v-model="date"
                                :is24="false"
                                :enableTimePicker="false"
                                :autoApply="true"
                                range
                                twoCalendars
                                placeholder="Select Date Range"
                                :format="formatDateRange"
                                weekStart=0
                            />
                        </div>

                        <div v-if="['outlet_sales', 'item_sales'].includes(form.report)" class="flex flex-col w-32">
                            <jet-label for="filter by" value="Filter by" />
                            <multiselect class="multi-sm min-w-32 h-7 text-base"
                                v-model="form.filter_by"
                                :options="filterOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canClear="false"
                                :canDeselect="false"
                                :classes="{singleLabel: 'w-full px-2'}"
                            />
                        </div>

                        <div v-if="form.report === 'outlet_sales'" class="flex flex-col flex-1">
                            <jet-label for="fundraising_status" value="Fundraising Status" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm whitespace-nowrap h-7 text-base"
                                v-model="form.fundraising_status"
                                :options="fundraisingStatusOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canDeselect="false"
                                placeholder="None"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                            />
                        </div>

                        <div v-if="form.report === 'outlet_sales'" class="flex flex-col flex-1">
                            <jet-label for="fundraising" value="Fundraising Program" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm whitespace-nowrap h-7 text-base"
                                v-model="form.fundraising_code"
                                :options="fundraisingCodeOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canDeselect="false"
                                placeholder="None"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                                :disabled="form.fundraising_status == null"
                            />
                        </div>

                        <div v-if="form.report === 'outlet_sales'" class="flex flex-col flex-1">
                            <jet-label for="location" value="Location" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm whitespace-nowrap h-7 text-base"
                                v-model="form.location"
                                :options="locations"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canDeselect="false"
                                placeholder="None"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                            />
                        </div>

                        <div v-if="form.report === 'item_sales'" class="flex flex-col w-32">
                            <jet-label for="shipping_method" value="Shipping method" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm whitespace-nowrap min-w-32 h-7 text-base"
                                v-model="form.shipping_method"
                                :options="shippingOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canDeselect="false"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                            />
                        </div>

                        <div v-if="form.report === 'item_sales' && form.shipping_method === 'pickup'" class="flex flex-col w-40">
                            <jet-label for="time" value="Time" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm whitespace-nowrap h-7 text-base"
                                v-model="form.time"
                                :options="timeOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canDeselect="false"
                                placeholder="All"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                            />
                        </div>

                        <div v-if="form.report === 'referral_campaign'" class="flex flex-col">
                            <jet-label for="claimed_by" value="Claimed By" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm whitespace-nowrap h-7 text-base"
                                v-model="form.claimed_by"
                                :options="claimedByOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canDeselect="false"
                                placeholder="Show all"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                            />
                        </div>

                        <div v-if="form.report === 'item_sales'" class="flex flex-col w-60 relative">
                            <jet-label for="purchaser_id" value="Purchaser" class="whitespace-nowrap"/>
                            <multiselect
                                v-model="form.purchaser_id"
                                :options="purchasers"
                                valueProp="id"
                                trackBy="id"
                                label="name"
                                placeholder="All Purchasers"
                                mode="multiple"
                                :closeOnSelect="false"
                                    class="multi-custom h-7 text-base"
                                :classes="{multipleLabel: 'w-full px-2'}"
                            >
                                <template v-slot:multiplelabel="{ values }">
                                    <div class="multiselect-multiple-label">
                                        {{ values.length }} purchaser{{ values.length > 1 ? 's' : ''}}
                                    </div>
                                </template>
                            </multiselect>
                            <div v-show="form.purchaser_id && form.purchaser_id.length" class="absolute -bottom-6 w-full truncate">
                                <span v-for="(single_purchaser_id, index) in form.purchaser_id" :key="single_purchaser_id" class="text-xs">
                                    {{ purchasers.find(p => p.id == single_purchaser_id).name }}{{index == form.purchaser_id.length - 1 ? '' : ', '}}
                                </span>
                            </div>
                        </div>

                        <div v-if="['customer_sales', 'referral_campaign'].includes(form.report)" class="flex flex-col w-60">
                            <jet-label for="search" value="Search" class="whitespace-nowrap"/>
                            <jet-input type="text" name="search" class="h-7 text-base" v-model="search" @input="filterData"/>
                        </div>

                        <div v-if="form.report === 'customer_sales' && Object.keys(report_data).length" class="flex flex-row items-center h-7">
                            <toggle id="active" class="py-1" v-model="showChart" />
                            <jet-label for="active" value="Chart" class="ml-2 inline-block" style="margin-bottom: 0px;" />
                        </div>

                        <div v-if="form.report === 'promo_codes'" class="ml-4 flex flex-col">
                            <jet-label for="gift_card_only" value="Gift Card only" class="mb-1 whitespace-nowrap" />
                            <toggle id="gift_card_only" v-model="form.gift_card_only" />
                        </div>

                        <div v-if="form.report === 'promo_codes'" class="flex flex-col">
                            <jet-label for="employee_code_only" value="Employee Code only" class="mb-1 whitespace-nowrap" />
                            <toggle id="employee_code_only" v-model="form.employee_code_only" />
                        </div>

                    </div>

                    <div class="xl:hidden self-end">
                        <jet-tertiary-button type="button" class="py-1 ml-4 h-7" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>

                </form>

                <div class="hidden xl:block self-end">
                    <jet-tertiary-button type="button" class="py-1 ml-4 h-7" @click="handleReset" >
                        Reset
                    </jet-tertiary-button>
                </div>
            </div>
        </div>

        <div class="px-8 py-6">
            <div v-if="!form.processing" >
                <div v-if="form.report != null && Object.keys(report_data).length">
                    <div v-if="!form.isDirty" class="rounded overflow-y-auto report-container">
                        <!-- Report Vue Components -->
                        <outlet-sales
                            v-if="form.report === 'outlet_sales' && !form.isDirty"
                            :data="report_data"
                            :sort_column="sort_column"
                            :sort_direction="sort_direction"
                            @sort="handleSort"
                        />
                        <item-sales
                            v-if="form.report === 'item_sales' && !form.isDirty"
                            :data="report_data"
                            :sort_column="sort_column"
                            :sort_direction="sort_direction"
                            @sort="handleSort"
                        />
                        <item-pick-zones
                            v-if="form.report === 'item_pick' && !form.isDirty"
                            :data="report_data"
                        />
                        <customer-sales
                            v-if="form.report === 'customer_sales' && !form.isDirty"
                            :data="report_data"
                            :show_chart="showChart"
                            :sort_column="sort_column"
                            :sort_direction="sort_direction"
                            @sort="handleSort"
                        />
                        <promo-codes
                            v-if="form.report === 'promo_codes' && !form.isDirty"
                            :data="report_data"
                        />
                        <referral-campaign
                            v-if="form.report === 'referral_campaign' && !form.isDirty"
                            :data="report_data"
                            :sort_column="sort_column"
                            :sort_direction="sort_direction"
                            @sort="handleSort"
                        />
                    </div>

                    <div v-else class="text-center text-gray-400 text-2xl pt-10">
                        Click Run Report to refresh data
                    </div>
                </div>

                <div v-show="showNoReportDataMsg" class="text-center text-gray-400 text-2xl pt-10">
                    No data for selected report parameters
                </div>

                <div v-show="showInitialReportMsg" class="text-center text-gray-400 text-2xl pt-10">
                    Select report parameters & click Run Report button
                </div>
            </div>

            <div v-else class="text-center text-gray-400 text-2xl pt-10">
                Retrieving report data...
            </div>
        </div>
    </app-layout>
</template>

<script>
import { DateTime } from "luxon";
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import ReportsNavigation from '@/Pages/Reports/ReportsNavigation';
import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import Datepicker from '@vuepic/vue-datepicker';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';

// Reports
import OutletSales from '@/Pages/Reports/Retail/OutletSales';
import ItemSales from '@/Pages/Reports/Retail/ItemSales';
import ItemPickZones from '@/Pages/Reports/Retail/ItemPickZones';
import CustomerSales from '@/Pages/Reports/Retail/CustomerSales';
import PromoCodes from '@/Pages/Reports/Retail/PromoCodes';
import ReferralCampaign from '@/Pages/Reports/Retail/ReferralCampaign';

import '@vuepic/vue-datepicker/dist/main.css';
import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const form = useForm({
            report: props.filters.report,
            filter_by: props.filters.filter_by ?? 'ship_date',
            from: props.filters.from,
            to: props.filters.to,
            fundraising_status: props.filters.fundraising_status,
            fundraising_code: props.filters.fundraising_code,
            location: props.filters.location,
            shipping_method: props.filters.shipping_method,
            time: props.filters.time,
            claimed_by: props.filters.claimed_by,
            gift_card_only: props.filters.gift_card_only ?? false,
            employee_code_only: props.filters.employee_code_only ?? false,
            purchaser_id: props.filters.purchaser_id ?? [],
        });

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        ReportsNavigation,
        JetButton,
        JetInput,
        JetInputError,
        JetLabel,
        JetTertiaryButton,
        Multiselect,
        Datepicker,
        Toggle,
        OutletSales,
        ItemSales,
        ItemPickZones,
        CustomerSales,
        PromoCodes,
        ReferralCampaign,
    },

    props: ['data', 'fundraising_codes_all', 'fundraising_codes_active', 'fundraising_codes_inactive', 'locations', 'filters', 'purchasers'],

    data() {
        return {
            report_data: _.cloneDeep(this.data),
            sort_column: null,
            sort_direction: 'asc',
            date: null,
            search: '',
            showChart: true,

            shippingOptions: [
                { value: 'pickup', label: 'Pickup' },
                { value: 'delivery', label: 'Delivery' },
            ],

            timeOptions: [
                { value: 'morning', label: 'Morning' },
                { value: 'afternoon', label: 'Afternoon' },
                { value: 'evening', label: 'Evening' },
            ],

            reportOptions: [
                { value: 'outlet_sales', label: 'Outlet Sales' },
                { value: 'item_sales', label: 'Item Sales' },
                { value: 'item_pick', label: 'Item Pick Zone' },
                { value: 'customer_sales', label: 'Customer Sales' },
                { value: 'promo_codes', label: 'Promo Codes' },
                { value: 'referral_campaign', label: 'Referral Campaign' },
            ],

            filterOptions: [
                { value: 'order_date', label: 'Order Date' },
                { value: 'ship_date', label: 'Ship Date' },
            ],

            fundraisingStatusOptions: [
                { value: 'all', label: 'All' },
                { value: '1', label: 'Active' },
                { value: '0', label: 'Inactive' },
            ],

            claimedByOptions: [
                { value: 'source', label: 'Source' },
                { value: 'friend', label: 'Friend' },
                { value: 'none', label: 'None' },
            ],
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.form.submit('post', route('reports.retail.report'), {
                preserveState: true,

                onSuccess: () => {
                    this.sort_column = this.defaultSortColumn;
                    this.sort_direction = this.defaultSortDirection;
                }
            });
        }, 250),

        filterData() {
            let search = this.search.toLowerCase();

            if (this.form.report === 'customer_sales') {
                this.report_data.results = _.filter(this.data.results, item => {
                    return item.shipping_email.toLowerCase().includes(search) ||
                            item.shipping_name.toLowerCase().includes(search) ||
                            item.order_count.toString().includes(search) ||
                            item.total_sales.toLowerCase().includes(search) ||
                            item.avg_sale.toString().includes(search);
                });
            }

            if (this.form.report === 'referral_campaign') {
                this.report_data.referrals = _.filter(this.data.referrals, item => {
                    return item.source_first_name.toLowerCase().includes(search) ||
                            item.source_name.toLowerCase().includes(search) ||
                            item.source_email.toLowerCase().includes(search) ||
                            item.source_promo_code.toLowerCase().includes(search) ||
                            item.friend_first_name.toLowerCase().includes(search) ||
                            item.friend_name.toLowerCase().includes(search) ||
                            item.friend_email.toLowerCase().includes(search) ||
                            item.friend_promo_code.toLowerCase().includes(search) ||
                            item.dateForSearch.toLowerCase().includes(search);
                });
            }
        },

        handleSort(sort, array) {
            this.report_data[array] = _.orderBy(this.report_data[array],[sort.column], [sort.direction])
            this.sort_column = sort.column;
            this.sort_direction = sort.direction;
        },

        handleReset() {
            this.report_data = [];
            this.sort_column = null;
            this.sort_direction = 'asc';
            this.date = null;
            this.search = '';
            this.showChart = true;

            this.form.report = null;
            this.form.filter_by = 'ship_date';
            this.form.from = null;
            this.form.to = null;
            this.form.fundraising_code = null;
            this.form.location = null;
            this.form.shipping_method = null;
            this.form.time = null;
            this.form.claimed_by = null;
            this.form.gift_card_only = false;
            this.form.employee_code_only = false;
            this.form.purchaser_id = [];
            this.handleSubmit();
        },

        handlePrint() {
            window.open((route('reports.retail.print', {
                report: this.form.report,
                filter_by: this.form.filter_by,
                from: this.form.from,
                to: this.form.to,
                fundraising_status: this.form.fundraising_status || '',
                fundraising_code: this.form.fundraising_code || '',
                location: this.form.location || '',
                shipping_method: this.form.shipping_method || '',
                time: this.form.time || '',
                claimed_by: this.form.claimed_by || '',
                gift_card_only: this.form.gift_card_only || '',
                employee_code_only: this.form.employee_code_only || '',
                purchaser_id: this.form.purchaser_id || []
            } )), "_blank");
        },

        setDateRange() {
            if (this.filters.from !== null && this.filters.to !== null) {
                this.date = [];
                this.date.push(this.formatDateToJS(this.filters.from));
                this.date.push(this.formatDateToJS(this.filters.to));
            }
        },

        handleUpdateDateRange(dateRange) {
            this.form.from = dateRange && dateRange.length ? DateTime.fromJSDate(dateRange[0]).toISODate() : null;
            this.form.to = dateRange && dateRange.length ? DateTime.fromJSDate(dateRange[1]).toISODate() : null;
        },

        formatDateToJS(date) {
            return DateTime.fromISO(date).toJSDate();
        },

        formatDateRange(dateRange) {
            const start = dateRange[0];
            const end = dateRange[1];

            if (start && end) {
                let thisYear = DateTime.now().toFormat('yyyy');
                let startYear = DateTime.fromJSDate(start).toFormat('yyyy');
                let endYear = DateTime.fromJSDate(end).toFormat('yyyy');
                let startDate = startYear === thisYear ? DateTime.fromJSDate(start).toFormat('LLL. d') : DateTime.fromJSDate(start).toFormat("LLL. d, yyyy");
                let endDate = endYear === thisYear ? DateTime.fromJSDate(end).toFormat('LLL. d') : DateTime.fromJSDate(end).toFormat("LLL. d, yyyy");

                return startDate + ' - ' + endDate;
            }

            return null;
        }
    },

    computed: {
        canRunReport() {
            return this.form.report && this.form.from && this.form.to;
        },
        defaultSortColumn() {
            switch (this.form.report) {
                case 'outlet_sales':
                    return 'ship_date_for_sort';
                case 'item_sales':
                    return 'title';
                case 'customer_sales':
                    return 'total_sales';
                case 'referral_campaign':
                    return 'created_at';
                default:
                    return null;
            }
        },
        defaultSortDirection() {
            return this.form.report === 'customer_sales' ? 'desc' : 'asc';
        },
        showInitialReportMsg() {
            return this.form.report == null ||
                (this.form.report != null && this.form.isDirty && Object.keys(this.report_data).length == 0 );
        },
        showNoReportDataMsg() {
            return this.form.report != null && !this.form.isDirty && Object.keys(this.report_data).length === 0;
        },

        fundraisingCodeOptions () {
            if (this.form.fundraising_status === 'all') return this.fundraising_codes_all;
            else if (this.form.fundraising_status === '1') return this.fundraising_codes_active;
            else if (this.form.fundraising_status === '0') return this.fundraising_codes_inactive;
            else return [];
        }
    },

    watch: {
        data: {
            immediate: true,
            handler (newValue) {
                this.report_data = _.cloneDeep(newValue);
            }
        },

        'form.fundraising_status' () {
            this.form.fundraising_code = null;
        }
    },

    mounted() {
        this.setDateRange();
    }
}
</script>

<style scoped>
.w-68 {
    width: 17rem;
}

.min-w-32 {
    min-width: 8rem;
}
.multi-custom {
    min-height: 1.5rem;
}
</style>

<style>
.dp-custom-input {
    @apply h-7;
}

.report-container thead {
    position: sticky;
    top: 0;
}
.report-container tfoot {
    position: sticky;
    bottom: 0;
}
.report-container tfoot td {
    height: 1.5rem;
    line-height: 1.5rem;
    background-color: #efefef;
    @apply border-gray border-b-0 border-t-2;
    @apply font-bold text-sm;
    @apply pt-2 pb-1 px-2;
}
</style>
